import { Minus, Package, Plus, Trash } from "@/components/icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  FieldLabel,
  Input,
  toast,
} from "@/components/kit";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  AD_HOC_KIT_DEFAULT_NAME,
  AD_HOC_KIT_VARIANT_ID,
} from "@/modules/orders/domain/ad-hoc-kit";
import { formatLak } from "@/shared/lib/currency";
import { useState } from "react";
import type { OrderLineItem, OrderLineKitComponent } from "./OrderLineItems";
import {
  ProductPickerModal,
  type PickedVariant,
} from "./ProductPickerModal";

type AdHocKitDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (item: OrderLineItem) => void;
};

type DraftComponent = OrderLineKitComponent;

export function AdHocKitDialog({
  open,
  onOpenChange,
  onConfirm,
}: AdHocKitDialogProps) {
  const isMobile = useIsMobile();
  const [kitName, setKitName] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [components, setComponents] = useState<DraftComponent[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  function reset() {
    setKitName("");
    setUnitPrice("");
    setComponents([]);
    setPickerOpen(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handlePickComponent(picked: PickedVariant) {
    if (picked.variantId === AD_HOC_KIT_VARIANT_ID) {
      toast.error("ບໍ່ສາມາດໃຊ້ລະບົບ placeholder ເປັນສ່ວນປະກອບ");
      return;
    }
    setComponents((prev) => {
      const existing = prev.find(
        (c) => c.componentVariantId === picked.variantId,
      );
      if (existing) {
        return prev.map((c) =>
          c.componentVariantId === picked.variantId
            ? { ...c, quantity: c.quantity + 1 }
            : c,
        );
      }
      return [
        ...prev,
        {
          componentVariantId: picked.variantId,
          quantity: 1,
          productName: picked.productName,
          variantName: picked.variantName,
          stockQuantity: picked.stockQuantity,
        },
      ];
    });
  }

  function changeQty(variantId: string, delta: number) {
    setComponents((prev) =>
      prev
        .map((c) =>
          c.componentVariantId === variantId
            ? { ...c, quantity: c.quantity + delta }
            : c,
        )
        .filter((c) => c.quantity > 0),
    );
  }

  function removeComponent(variantId: string) {
    setComponents((prev) =>
      prev.filter((c) => c.componentVariantId !== variantId),
    );
  }

  function handleConfirm() {
    if (components.length === 0) {
      toast.error("ເລືອກສ່ວນປະກອບຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }
    const price = Number(unitPrice);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("ລາຄາຊຸດບໍ່ຖືກຕ້ອງ");
      return;
    }

    const short = components.find(
      (c) => c.stockQuantity < c.quantity,
    );
    if (short) {
      toast.error(
        `ສະຕັອກບໍ່ພໍ: ${short.productName} · ${short.variantName}`,
      );
      return;
    }

    const name = kitName.trim() || AD_HOC_KIT_DEFAULT_NAME;
    const lineKey = `ad_hoc_kit:${crypto.randomUUID()}`;

    onConfirm({
      lineKey,
      lineType: "ad_hoc_kit",
      kitName: kitName.trim() || null,
      kitComponents: components,
      variantId: AD_HOC_KIT_VARIANT_ID,
      productName: AD_HOC_KIT_DEFAULT_NAME,
      variantName: name,
      saleUnitPrice: price.toFixed(2),
      imageKey: null,
      stockQuantity: 0,
      stockUnitLabel: "ຊຸດ",
      unitsPerSale: 1,
      quantity: 1,
      isBonus: false,
      lotPicks: [],
    });
    handleOpenChange(false);
  }

  const body = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <FieldLabel>ຊື່ຊຸດ (ທາງເລືອກ)</FieldLabel>
        <Input
          value={kitName}
          onChange={(e) => setKitName(e.target.value)}
          placeholder={AD_HOC_KIT_DEFAULT_NAME}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel>ລາຄາຊຸດ (LAK)</FieldLabel>
        <Input
          inputMode="decimal"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className="font-medium text-sm">ສ່ວນປະກອບ</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => setPickerOpen(true)}
          >
            <Plus data-icon="inline-start" />
            ເພີ່ມສິນຄ້າ
          </Button>
        </div>

        {components.length === 0 ? (
          <p className="rounded-xl border border-dashed px-3 py-6 text-center text-muted-foreground text-xs">
            ຍັງບໍ່ມີສ່ວນປະກອບ — ກົດເພີ່ມສິນຄ້າ (ບໍ່ຮອງຮັບເຊັດຊ້ອນ)
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {components.map((comp) => {
              const short = comp.stockQuantity < comp.quantity;
              return (
                <li
                  key={comp.componentVariantId}
                  className={cn(
                    "flex items-center gap-2 rounded-xl border px-3 py-2",
                    short
                      ? "border-destructive/40 bg-destructive/5"
                      : "border-secondary/20",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">
                      {comp.productName}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {comp.variantName}
                      <span className="tabular-nums">
                        {" "}
                        · ຄົງ {comp.stockQuantity}
                      </span>
                    </p>
                  </div>
                  <div className="inline-flex items-center rounded-full border border-secondary/25 p-0.5">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 rounded-full"
                      onClick={() =>
                        changeQty(comp.componentVariantId, -1)
                      }
                    >
                      <Minus />
                    </Button>
                    <span className="min-w-7 text-center text-sm tabular-nums">
                      {comp.quantity}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 rounded-full"
                      onClick={() => changeQty(comp.componentVariantId, 1)}
                    >
                      <Plus />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 rounded-full text-destructive"
                    onClick={() =>
                      removeComponent(comp.componentVariantId)
                    }
                  >
                    <Trash />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {unitPrice !== "" && Number.isFinite(Number(unitPrice)) ? (
        <p className="text-right font-semibold text-secondary tabular-nums">
          ລາຄາຊຸດ {formatLak(Number(unitPrice))}
        </p>
      ) : null}
    </div>
  );

  const footer = (
    <Button
      type="button"
      className="w-full rounded-full"
      onClick={handleConfirm}
      disabled={components.length === 0}
    >
      <Package data-icon="inline-start" />
      ເພີ່ມຊຸດເຂົ້າອໍເດີ້
    </Button>
  );

  return (
    <>
      {isMobile ? (
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerContent className="max-h-[92dvh] px-4 pb-4">
            <DrawerHeader className="px-0 text-left">
              <DrawerTitle>ຈັດເປັນເຊັດ</DrawerTitle>
              <DrawerDescription>
                ເລືອກສ່ວນປະກອບ + ລາຄາຊຸດ — ຫັກສະຕັອກຊິ້ນຍ່ອຍເມື່ອຊຳລະ
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto pb-2">{body}</div>
            <DrawerFooter className="px-0">{footer}</DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>ຈັດເປັນເຊັດ</DialogTitle>
              <DialogDescription>
                ເລືອກສ່ວນປະກອບ + ລາຄາຊຸດ — ຫັກສະຕັອກຊິ້ນຍ່ອຍເມື່ອຊຳລະ
              </DialogDescription>
            </DialogHeader>
            {body}
            <DialogFooter>{footer}</DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePickComponent}
        purpose="order"
        excludeBundles
        subtitle="ເລືອກສ່ວນປະກອບຂອງຊຸດ (ບໍ່ຮອງຮັບເຊັດ)"
      />
    </>
  );
}
